import { Box, Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import Image from 'next/image';
import { TourAvailabilityWizard, useTourUpsellCookie } from '@zg-rentals/touring';
import { useState } from 'react';
import IconStarsGroup from '../../Icons/IconStarsGroup';
import calendarImage from '../../../images/set-availability.png';
import useModal from '../../../hooks/useModal';
import {
  emitLeadMgmtTourUpsellModalClickEvent,
  LEAD_MNGMT_TOUR_UPSELL_MODAL_CLICK_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/tourScheduling';
import { ModalType } from '../../../types/ModalType';
import { useAuth } from '../../../AuthProvider';

import '@zg-rentals/touring/src/styles/tourCalendar.scss';

type TourSchedulingUpsellModalProps = {
  listingAlias: string;
  modalHeaderText: string;
};

export default function TourSchedulingUpsellModal({ listingAlias, modalHeaderText }: TourSchedulingUpsellModalProps) {
  const [shouldDisplayTourAvailabilityWizard, setDisplayTourAvailabilityWizard] = useState(false);
  const { user } = useAuth();
  const { clearModal, activeModal } = useModal();
  const { setUpsellCookie } = useTourUpsellCookie(listingAlias);

  const handleTourAvailabilityClick = () => {
    emitLeadMgmtTourUpsellModalClickEvent(user, { listingAlias });
    setDisplayTourAvailabilityWizard(true);
    clearModal();
  };

  const handleCloseModal = (isOpen: boolean) => {
    if (!isOpen) {
      setUpsellCookie();
      clearModal();
    }
  };

  return (
    <>
      <Modal
        dividers
        open={activeModal === ModalType.TourSchedulingUpsellModal}
        onOpenChange={handleCloseModal}
        shouldAwaitInteractionResponse
        size={{
          base: 'fullScreen',
          lg: 'md',
        }}
        trigger={null}
        body={
          <div data-testid="tour-scheduling-upsell-modal">
            <Box css={{ position: 'relative' }}>
              <Box
                css={{
                  height: '312px',
                  backgroundColor: 'purple.100',
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <Box
                  css={{
                    position: 'relative',
                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.30)',
                    borderRadius: '12px',
                    marginTop: 'looser',
                    height: '312px',
                  }}
                >
                  <Box css={{ position: 'absolute', right: '-4%', top: '-3.5%' }}>
                    <IconStarsGroup isGroupOfThreeStars />
                  </Box>
                  <Box css={{ position: 'absolute', left: '-7%', bottom: '38%' }}>
                    <IconStarsGroup isGroupOfThreeStars={false} />
                  </Box>
                  <Image
                    src={calendarImage}
                    alt="Image of a calendar"
                    height={436}
                    style={{
                      borderRadius: '12px',
                      objectFit: 'cover',
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                    priority={true}
                  />
                </Box>
              </Box>

              <Box css={{ padding: 'looser', backgroundColor: 'white', position: 'relative', zIndex: '1' }}>
                <Heading level={4}>Book tours easier, find renters faster</Heading>
                <Paragraph css={{ marginTop: 'default' }}>
                  New booking options help you schedule more efficiently than ever. To get started, just add your tour
                  availability.
                </Paragraph>
              </Box>
            </Box>
          </div>
        }
        footer={
          <Button
            data-eventid={LEAD_MNGMT_TOUR_UPSELL_MODAL_CLICK_EVENT_TYPE_ID}
            emphasis="filled"
            tone="brand"
            onClick={handleTourAvailabilityClick}
          >
            Add tour availability
          </Button>
        }
        css={{
          padding: '0 !important',
          '& div:first-of-type': {
            padding: '0',
            borderTopLeftRadius: '200',
            borderTopRightRadius: '200',
          },
        }}
      />

      {shouldDisplayTourAvailabilityWizard && (
        <TourAvailabilityWizard
          listingAlias={listingAlias}
          modalHeaderText={modalHeaderText}
          onClose={() => setDisplayTourAvailabilityWizard(false)}
        />
      )}
    </>
  );
}
